// src/pages/About.jsx
import {
    Container,
    Typography,
    Box,
    Paper,
    keyframes,
    Grid,
    Button,
} from "@mui/material";

import GroupsIcon from "@mui/icons-material/Groups";
import StarIcon from "@mui/icons-material/Star";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import BoltIcon from "@mui/icons-material/Bolt";
import PersonIcon from "@mui/icons-material/Person";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function About() {
    return (
        <Container maxWidth="lg" sx={{ mt: 6, mb: 10 }}>
            {/* HERO */}
            <Box
                sx={{
                    position: "relative",
                    height: {
                        xs: "auto",   // 🔥 laisse la hauteur s’adapter au texte
                        md: 320,      // desktop reste fixe
                    },
                    py: { xs: 6, md: 0 }, // 🔥 ajoute du padding vertical en mobile
                    borderRadius: 3,
                    overflow: "hidden",
                    mb: 8,
                    animation: `${fadeInUp} 0.5s ease-out`,
                }}
            >
                <Box
                    component="img"
                    src="/depart-thyon.jpg"
                    alt="Départ FIS Thyon"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(0.7)",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "linear-gradient(135deg, rgba(15,42,74,0.7), rgba(26,77,143,0.4))",
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        textAlign: "center",
                        color: "white",
                        px: 2,
                    }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            mb: 1,
                            textShadow: "0 3px 10px rgba(0,0,0,0.4)",
                        }}
                    >
                        À propos de 321‑TIMING.ch
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 400,
                            opacity: 0.9,
                            textShadow: "0 2px 6px rgba(0,0,0,0.4)",
                        }}
                    >
                        Une expertise au service du chronométrage sportif.
                    </Typography>
                </Box>
            </Box>

            {/* INTRO */}
            <Box
                sx={{
                    mb: 6,
                    textAlign: "center",
                    animation: `${fadeInUp} 0.7s ease-out`,
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: "720px",
                        margin: "0 auto",
                        color: "text.secondary",
                    }}
                >
                    Une association dédiée au chronométrage sportif, fondée par deux passionnés
                    qui mettent leur expertise au service des clubs, des organisateurs et des athlètes.
                </Typography>
            </Box>

            {/* ASSOCIATION */}
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 4,
                    animation: `${fadeInUp} 0.8s ease-out`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            backgroundColor: "primary.light",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                        }}
                    >
                        <GroupsIcon sx={{ color: "white" }} />
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        L’association
                    </Typography>
                </Box>

                <Typography variant="body1">
                    L’association 321‑timing.ch a été fondée par <strong>Stéphane Veya</strong> et{" "}
                    <strong>Christoph Wenger</strong>. Elle vise à promouvoir la formation et le
                    perfectionnement dans le domaine du chronométrage, en appliquant les meilleures
                    pratiques professionnelles et en utilisant des technologies modernes, fiables et éprouvées.
                </Typography>
            </Paper>

            {/* VALEURS */}
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 6,
                    animation: `${fadeInUp} 0.9s ease-out`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            backgroundColor: "secondary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                        }}
                    >
                        <StarIcon sx={{ color: "white" }} />
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Nos valeurs
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <PrecisionManufacturingIcon sx={{ color: "secondary.main", mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Précision
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            Chaque centième compte. Nous garantissons des mesures fiables et irréprochables.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <BoltIcon sx={{ color: "secondary.main", mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Réactivité
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            Une organisation efficace et une adaptation rapide aux conditions de terrain.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <GroupsIcon sx={{ color: "secondary.main", mr: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Esprit d’équipe
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            Collaborer avec clubs, organisateurs et athlètes pour garantir le succès de chaque événement.
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* PORTRAITS */}
            <Box
                sx={{
                    mb: 6,
                    animation: `${fadeInUp} 1.0s ease-out`,
                }}
            >
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mb: 3, textAlign: "center", color: "primary.main" }}
                >
                    Les fondateurs
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <PersonIcon sx={{ color: "primary.main", mr: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    Stéphane Veya
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                Plus de 15 ans d’expérience en chronométrage. Avocat de formation,
                                passionné par la mise en valeur des événements sportifs et la qualité
                                de leur organisation.
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <PersonIcon sx={{ color: "primary.main", mr: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    Christoph Wenger
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                Plus de 20 ans d’expérience en chronométrage. Ingénieur de formation,
                                passionné par les défis techniques et la recherche de solutions adaptées
                                à chaque événement.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* CTA */}
            {/* SECTION — Contact (version card premium) */}
            <Grid
                container
                direction="column"
                alignItems="center"
                sx={{
                    width: "100%",
                    maxWidth: 700,
                    margin: "0 auto",
                    px: 2,
                    mt: 10,
                }}
            >
                <Grid item xs={12} sx={{ width: "100%" }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            mb: 4,
                            animation: `${fadeInUp} 0.8s ease-out`,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    backgroundColor: "secondary.light",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mr: 2,
                                }}
                            >
                            <GroupsIcon sx={{ color: "white" }} />
                        </Box>
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                            Vous êtes à la recherche d'un partenaire chronométrage?
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
                            Contactez‑nous pour une offre personnalisée ou pour toute question.
                        </Typography>

                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            href="mailto:info@321-timing.ch"
                        >
                            Nous contacter
                        </Button>
                    </Paper>
                </Grid>
            </Grid>

        </Container>
    );
}
